/**
 * Where a section's poster image lives.
 *
 * Two storage generations have to be served, and neither is going away:
 *
 *   mobile uploads   publish an explicit `paths.thumbnail`
 *   legacy website   publish nothing, and the poster has to be derived from the dataset's own
 *                    base path as `section_{N}/frames/frame_0000.jpg`
 *
 * Extracted from SectionsDrawer, which is no longer the only thing that needs a poster: the
 * section chip labels rooms with them and the depth viewer's doorway paints the next room's
 * poster into the rim. Three copies of a two-layout fallback is how they drift apart.
 *
 * `authToken` is a parameter rather than a `useAuthStore` read so these stay callable outside a
 * component — the doorway resolves posters inside a store selector.
 */
import { resolveUrl, toBlobUrl } from '@/config'
import type { Dataset } from '@/types/dataset'
import type { ShopMetadata } from '@/types/shopMetadata'

/** The poster for a USER section (a room the owner named), given its dataset. */
export function userSectionThumbnail(dataset: Dataset, authToken: string | null): string {
  if (dataset.paths?.thumbnail) {
    return toBlobUrl(resolveUrl(dataset.paths.thumbnail))
  }
  const metadataPath = dataset.paths?.metadata
  if (!metadataPath) return ''
  const basePath = metadataPath.replace('/shop_metadata.json', '')
  const tokenParam = authToken ? `&token=${authToken}` : ''
  return toBlobUrl(resolveUrl(`${basePath}/section_0/frames/frame_0000.jpg?shop=${dataset.shopId}${tokenParam}`))
}

/** The poster for a COLMAP section (a detected component), given its numeric index. */
export function colmapSectionThumbnail(
  shopId: string | null,
  sectionId: number,
  shopMetadata: ShopMetadata | null,
  availableDatasets: Dataset[],
  authToken: string | null,
): string {
  if (!shopId) return ''

  // Prefer the explicit thumbnail path published in shop_metadata.json (mobile shops).
  const metaSection = shopMetadata?.sections.find((s) => s.section_id === sectionId)
  if (metaSection?.paths?.thumbnail) {
    return toBlobUrl(resolveUrl(metaSection.paths.thumbnail))
  }

  // Legacy website scans: derive from dataset base path + section_{N}/frames/frame_0000.jpg.
  const dataset = availableDatasets.find((d) => d.id === shopId)
  const datasetBasePath =
    dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
  const framesPath = `${datasetBasePath}/section_${sectionId}/frames`
  const tokenParam = authToken ? `&token=${authToken}` : ''
  return toBlobUrl(resolveUrl(`${framesPath}/frame_0000.jpg?shop=${shopId}${tokenParam}`))
}
